import styles from "./dnumeral.module.css";

/**
 * DNumeral — the blog's monogram: a giant Fraunces numeral.
 *
 * It numbers the WRITING, and the writing ships weekly, so the number it
 * carries is an entry's week of its arc. (The live day counters are a
 * different component — DayCounter — because a day and a published week are
 * different facts.)
 *
 * Server-safe (no client JS, no state). Typography rides the blog layout's
 * `--font-fraunces` variable (loaded by the blog layout via next/font) with a
 * serif fallback, `tabular-nums`, and Fraunces' display axes (`opsz` 144,
 * `WONK` 1) for the editorial voice.
 *
 * Sizing: defaults to the 120-200px hero scale via a fluid clamp. Callers
 * override by setting the `--dnum-size` custom property through `className`
 * (e.g. cube faces / list thumbnails use a smaller scale):
 *
 *   .myNumeral { --dnum-size: 3rem; }
 *   <DNumeral week={2} total={13} className={s.myNumeral} />
 *
 * The optional `of N` suffix renders in meta style (13-14px, muted). The
 * muted tone is derived from `currentColor` via color-mix so the component
 * is correct in both blog themes without knowing the token names.
 */
export type DNumeralProps = {
  /** 1-based week number of the arc. */
  week: number;
  /** Arc length in weeks — renders a small muted "of N" suffix when given. */
  total?: number;
  /** Extra classes; use to override `--dnum-size` or set color. */
  className?: string;
};

export function DNumeral({ week, total, className }: DNumeralProps) {
  const label = total != null ? `Week ${week} of ${total}` : `Week ${week}`;
  return (
    <span
      role="img"
      aria-label={label}
      className={className ? `${styles.numeral} ${className}` : styles.numeral}
    >
      <span aria-hidden="true" className={styles.digits}>
        {week}
      </span>
      {total != null ? (
        <span aria-hidden="true" className={styles.suffix}>
          of {total}
        </span>
      ) : null}
    </span>
  );
}

export default DNumeral;
